"use client";

import { useMemo, useState } from "react";
import {
  Button,
  Circle as CircleIcon,
  Clock,
  Download,
  Hexagon,
  Icon,
  IconButton,
  Input,
  LandPlot,
  Label,
  Lock,
  Route,
  Select,
  Spline,
  Swatch,
  Trash2,
  cx,
} from "@/components";
import { getMapTokens, type MapSpec, type PointSet } from "@/lib/map";
import type { AnalysisTool, AnalysisToolFlags } from "@/config/brand.schema";
import {
  analysisToolGates,
  applyAnalysisSpec,
  buildAutomatedTerritories,
  buildDriveTime,
  buildRouteOptimization,
  emptyAnalysis,
  routeExportCsv,
  routeExportRows,
  MAX_ROUTE_STOPS,
  territoryColor,
  type AnalysisEntitlement,
  type AnalysisSpec,
  type BoundaryKind,
  type BoundaryProvider,
} from "@/lib/analysis";
import { assignViaProvider } from "@/lib/analysis/boundary";
import {
  createStubIsochroneProvider,
  haversineRoutingProvider,
  type RoutingProvider,
} from "@/lib/geo/routing";
import { driveTimeSummary, radiusSummary, routeSummary, territorySummary } from "./analysisLayers";

/** The draw request the panel hands up to the editor (which owns the canvas seam). */
export type AnalysisDrawRequest =
  | { kind: "radius" }
  | { kind: "lasso"; name: string; color: string };

export interface AnalysisPanelProps {
  pointSet: PointSet;
  spec: MapSpec;
  themeName: string;
  idPrefix: string;
  /** White-label brand name for the (brand-free-otherwise) locked-tool copy. */
  brandName: string;
  /** Per-tool build-go flags (from the white-label config). */
  toolFlags: AnalysisToolFlags;
  /** Tier-derived entitlement (never usage-derived). */
  entitlement: AnalysisEntitlement;
  /**
   * Where the locked-tool upgrade link points (FAIL-479). Defaults to /pricing —
   * mirrors every other locked surface (OverlayPanel, the Sheets method, the
   * quota UpsellModal) so a Free user who hits the analysis gate has a path out.
   */
  upgradeHref?: string;
  /** Active draw gesture, or null. */
  drawMode: "radius" | "lasso" | null;
  onSpecChange: (next: MapSpec) => void;
  onBeginDraw: (req: AnalysisDrawRequest) => void;
  onCancelDraw: () => void;
  /**
   * geo-core boundary provider — bound at DEPLOY TIME. Absent in the wedge, so
   * boundary mode surfaces the "configured at deployment" note instead of firing.
   */
  boundaryProvider?: BoundaryProvider;
  /**
   * geo-core routing provider for route optimization. Defaults to the OSS-first
   * haversine provider (fully client-computable), so route-opt works now; a
   * deployment can bind a road-network provider without touching the panel.
   */
  routingProvider?: RoutingProvider;
  /**
   * geo-core isochrone provider for drive-time. Defaults to the labeled stub
   * (straight-line approximation) until a real routing engine is configured.
   */
  isochroneProvider?: RoutingProvider;
}

const TOOL_META: Record<AnalysisTool, { label: string; hint: string }> = {
  radius: { label: "Radius circles", hint: "Draw a circle to count the points inside." },
  territoriesLasso: { label: "Lasso territory", hint: "Draw a shape to group the points it encloses." },
  territoriesAutomated: { label: "Automated territories", hint: "Split points into balanced territories." },
  territoriesBoundary: { label: "Boundary territories", hint: "Assign points to ZIP, county, or state." },
  driveTime: { label: "Drive time", hint: "Show reachable-area bands around your points." },
  routes: { label: "Route optimization", hint: "Order the stops into the shortest visiting route." },
};

/**
 * The M8 analysis panel — one progressively-disclosed, token-only surface. Each
 * tool is gated by its build-go flag (absent when off) AND the M7 entitlement
 * (a locked card when unentitled). Composes ONLY from the M1 primitives; every
 * color/space value is a token. Radius + lasso hand a draw request up to the
 * editor (which owns the canvas); automated + boundary compute in place.
 */
export function AnalysisPanel({
  pointSet,
  spec,
  themeName,
  idPrefix,
  brandName,
  toolFlags,
  entitlement,
  upgradeHref = "/pricing",
  drawMode,
  onSpecChange,
  onBeginDraw,
  onCancelDraw,
  boundaryProvider,
  routingProvider,
  isochroneProvider,
}: AnalysisPanelProps) {
  const tokens = useMemo(() => getMapTokens(themeName), [themeName]);
  const gates = useMemo(() => analysisToolGates(toolFlags, entitlement), [toolFlags, entitlement]);
  // OSS-first defaults — both fully client-computable, so the tools work now.
  const routing = useMemo(() => routingProvider ?? haversineRoutingProvider(), [routingProvider]);
  const isochrone = useMemo(() => isochroneProvider ?? createStubIsochroneProvider(), [isochroneProvider]);

  const [autoCount, setAutoCount] = useState(4);
  const [balanceBy, setBalanceBy] = useState("");
  const [boundaryKind, setBoundaryKind] = useState<BoundaryKind>("zip");
  const [boundaryNote, setBoundaryNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [driveMinutes, setDriveMinutes] = useState(15);
  const [driveNote, setDriveNote] = useState<string | null>(null);
  const [routeNote, setRouteNote] = useState<string | null>(null);

  const numericColumns = pointSet.columns.filter((c) => c.type === "number");
  const analysis = spec.analysis;
  const territories = analysis?.territories?.items ?? [];

  const nextLassoColor = territoryColor(tokens.palette, territories.length);

  function writeAnalysis(mut: (a: AnalysisSpec) => AnalysisSpec) {
    onSpecChange(applyAnalysisSpec(spec, mut(analysis ?? emptyAnalysis())));
  }

  /** Drive-time: request isochrone bands from the origin (metered → gated card). */
  async function runDriveTime() {
    if (!gates.driveTime.openable) return;
    setBusy(true);
    setDriveNote(null);
    try {
      const { driveTime, approximate, attribution } = await buildDriveTime(
        pointSet,
        "selected",
        [{ minutes: Math.max(1, driveMinutes) }],
        isochrone,
        { palette: tokens.palette },
      );
      writeAnalysis((a) => ({ ...a, driveTime }));
      setDriveNote(
        approximate
          ? `${attribution}. Configure a routing engine for road-network drive times.`
          : attribution,
      );
    } finally {
      setBusy(false);
    }
  }

  /** Route optimization: order the loaded stop set (metered → gated card). */
  async function runRoutes() {
    if (!gates.routes.openable) return;
    setBusy(true);
    setRouteNote(null);
    try {
      const stopRowIds = pointSet.points.map((p) => p.id);
      const res = await buildRouteOptimization(pointSet, stopRowIds, routing);
      if (!res.ok) {
        setRouteNote(
          res.reason === "too_many_stops"
            ? `Route optimization is capped at ${MAX_ROUTE_STOPS} stops — you have ${res.requested}. Filter to a smaller set first.`
            : "Add at least two points to optimize a route.",
        );
        return;
      }
      writeAnalysis((a) => ({ ...a, routes: res.routes }));
      setRouteNote(res.attribution);
    } finally {
      setBusy(false);
    }
  }

  /** Export the optimized stop order as a CSV download. */
  function exportRoute() {
    if (!analysis?.routes) return;
    const labelCol = pointSet.columns.find((c) => c.type === "text")?.name;
    const csv = routeExportCsv(routeExportRows(pointSet, analysis.routes, labelCol));
    if (typeof window === "undefined") return;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "route.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function removeDriveTime() {
    writeAnalysis((a) => ({ ...a, driveTime: undefined }));
    setDriveNote(null);
  }
  function removeRoutes() {
    writeAnalysis((a) => ({ ...a, routes: undefined }));
    setRouteNote(null);
  }

  function runAutomated() {
    const items = buildAutomatedTerritories(pointSet, {
      count: Math.max(1, autoCount),
      balanceBy: balanceBy || undefined,
      palette: tokens.palette,
    });
    writeAnalysis((a) => ({
      ...a,
      territories: { mode: "automated", items, balanceBy: balanceBy || undefined },
    }));
  }

  async function runBoundary() {
    if (!boundaryProvider) {
      setBoundaryNote("Boundary data is configured at deployment. Ask your administrator to enable it.");
      return;
    }
    setBusy(true);
    setBoundaryNote(null);
    try {
      const { territories: items } = await assignViaProvider(pointSet, boundaryKind, boundaryProvider, {
        palette: tokens.palette,
        balanceBy: balanceBy || undefined,
      });
      if (items.length === 0) {
        setBoundaryNote("No boundaries of that kind cover your points.");
        return;
      }
      writeAnalysis((a) => ({
        ...a,
        territories: { mode: "boundary", items, balanceBy: balanceBy || undefined },
      }));
    } finally {
      setBusy(false);
    }
  }

  function removeTerritory(id: string) {
    writeAnalysis((a) => {
      const items = (a.territories?.items ?? []).filter((t) => t.id !== id);
      return { ...a, territories: items.length ? { ...a.territories!, items } : undefined };
    });
  }
  function removeRadius(id: string) {
    writeAnalysis((a) => ({ ...a, radii: (a.radii ?? []).filter((r) => r.id !== id) }));
  }
  function clearTerritories() {
    writeAnalysis((a) => ({ ...a, territories: undefined }));
  }

  const territoryRows = territorySummary(analysis);
  const radiusRows = radiusSummary(analysis);
  const driveRows = driveTimeSummary(analysis);
  const routeRow = routeSummary(analysis);

  /** Locked card — build-go on, but the account isn't entitled yet. */
  const lockedCard = (tool: AnalysisTool) => (
    <div className="analysis-tool analysis-tool--locked" key={tool}>
      <span className="analysis-tool__head">
        <Icon icon={Lock} size="sm" />
        <span className="analysis-tool__title">{TOOL_META[tool].label}</span>
      </span>
      <p className="analysis-tool__hint">Available on a {brandName} paid plan.</p>
    </div>
  );

  const drawing = (kind: "radius" | "lasso") =>
    drawMode === kind ? (
      <Button variant="secondary" size="sm" onClick={onCancelDraw}>
        Cancel — click the map to finish
      </Button>
    ) : null;

  // FAIL-479: at least one tool is build-go on but not entitled → show a single
  // upgrade link below the stack (not one per locked card). Mirrors OverlayPanel.
  const anyLocked = Object.values(gates).some((g) => g.present && !g.openable);

  return (
    <div className="analysis-panel__body">
      {/* ---------------------------------------------------------- Radius */}
      {gates.radius.present &&
        (gates.radius.openable ? (
          <div className="analysis-tool">
            <span className="analysis-tool__head">
              <Icon icon={CircleIcon} size="sm" />
              <span className="analysis-tool__title">{TOOL_META.radius.label}</span>
            </span>
            <p className="analysis-tool__hint">{TOOL_META.radius.hint}</p>
            {drawMode === "radius" ? (
              drawing("radius")
            ) : (
              <Button variant="secondary" size="sm" onClick={() => onBeginDraw({ kind: "radius" })}>
                Draw a circle
              </Button>
            )}
            {radiusRows.length > 0 && (
              <ul className="analysis-list">
                {radiusRows.map((r) => (
                  <li key={r.id} className="analysis-list__row">
                    <span className="analysis-list__label">{r.label}</span>
                    <span className="analysis-list__count">{r.count.toLocaleString()} inside</span>
                    <IconButton label={`Remove ${r.label}`} variant="ghost" onClick={() => removeRadius(r.id)}>
                      <Icon icon={Trash2} size="sm" />
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          lockedCard("radius")
        ))}

      {/* ---------------------------------------------------------- Lasso */}
      {gates.territoriesLasso.present &&
        (gates.territoriesLasso.openable ? (
          <div className="analysis-tool">
            <span className="analysis-tool__head">
              <Icon icon={Spline} size="sm" />
              <span className="analysis-tool__title">{TOOL_META.territoriesLasso.label}</span>
            </span>
            <p className="analysis-tool__hint">{TOOL_META.territoriesLasso.hint}</p>
            {drawMode === "lasso" ? (
              drawing("lasso")
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  onBeginDraw({ kind: "lasso", name: `Territory ${territories.length + 1}`, color: nextLassoColor })
                }
              >
                Draw a territory
              </Button>
            )}
          </div>
        ) : (
          lockedCard("territoriesLasso")
        ))}

      {/* ------------------------------------------------------ Automated */}
      {gates.territoriesAutomated.present &&
        (gates.territoriesAutomated.openable ? (
          <div className="analysis-tool">
            <span className="analysis-tool__head">
              <Icon icon={LandPlot} size="sm" />
              <span className="analysis-tool__title">{TOOL_META.territoriesAutomated.label}</span>
            </span>
            <p className="analysis-tool__hint">{TOOL_META.territoriesAutomated.hint}</p>
            <div className="analysis-tool__row">
              <Label htmlFor={`${idPrefix}-auto-count`}>How many</Label>
              <Input
                id={`${idPrefix}-auto-count`}
                type="number"
                min={1}
                value={String(autoCount)}
                onChange={(e) => setAutoCount(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <div className="analysis-tool__row">
              <Label htmlFor={`${idPrefix}-auto-balance`}>Balance by</Label>
              <Select
                id={`${idPrefix}-auto-balance`}
                value={balanceBy}
                onChange={(e) => setBalanceBy(e.target.value)}
              >
                <option value="">Point count</option>
                {numericColumns.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="secondary" size="sm" onClick={runAutomated}>
              {analysis?.territories?.mode === "automated" ? "Re-run" : "Build territories"}
            </Button>
          </div>
        ) : (
          lockedCard("territoriesAutomated")
        ))}

      {/* ------------------------------------------------------- Boundary */}
      {gates.territoriesBoundary.present &&
        (gates.territoriesBoundary.openable ? (
          <div className="analysis-tool">
            <span className="analysis-tool__head">
              <Icon icon={Hexagon} size="sm" />
              <span className="analysis-tool__title">{TOOL_META.territoriesBoundary.label}</span>
            </span>
            <p className="analysis-tool__hint">{TOOL_META.territoriesBoundary.hint}</p>
            <div className="analysis-tool__row">
              <Label htmlFor={`${idPrefix}-boundary-kind`}>Boundary</Label>
              <Select
                id={`${idPrefix}-boundary-kind`}
                value={boundaryKind}
                onChange={(e) => setBoundaryKind(e.target.value as BoundaryKind)}
              >
                <option value="zip">ZIP code</option>
                <option value="county">County</option>
                <option value="state">State</option>
              </Select>
            </div>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void runBoundary()}>
              Assign to boundaries
            </Button>
            {boundaryNote && (
              <p className="analysis-tool__note" role="status">
                {boundaryNote}
              </p>
            )}
          </div>
        ) : (
          lockedCard("territoriesBoundary")
        ))}

      {/* ------------------------------------------------------ Drive time */}
      {gates.driveTime.present &&
        (gates.driveTime.openable ? (
          <div className="analysis-tool">
            <span className="analysis-tool__head">
              <Icon icon={Clock} size="sm" />
              <span className="analysis-tool__title">{TOOL_META.driveTime.label}</span>
            </span>
            <p className="analysis-tool__hint">{TOOL_META.driveTime.hint}</p>
            <div className="analysis-tool__row">
              <Label htmlFor={`${idPrefix}-drive-minutes`}>Minutes</Label>
              <Input
                id={`${idPrefix}-drive-minutes`}
                type="number"
                min={1}
                value={String(driveMinutes)}
                onChange={(e) => setDriveMinutes(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void runDriveTime()}>
              {analysis?.driveTime ? "Re-run" : "Show drive time"}
            </Button>
            {analysis?.driveTime && (
              <Button variant="ghost" size="sm" onClick={removeDriveTime}>
                Clear drive time
              </Button>
            )}
            {driveNote && (
              <p className="analysis-tool__note" role="status">
                {driveNote}
              </p>
            )}
          </div>
        ) : (
          lockedCard("driveTime")
        ))}

      {/* ------------------------------------------------ Route optimization */}
      {gates.routes.present &&
        (gates.routes.openable ? (
          <div className="analysis-tool">
            <span className="analysis-tool__head">
              <Icon icon={Route} size="sm" />
              <span className="analysis-tool__title">{TOOL_META.routes.label}</span>
            </span>
            <p className="analysis-tool__hint">
              {TOOL_META.routes.hint} Up to {MAX_ROUTE_STOPS} stops.
            </p>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void runRoutes()}>
              {routeRow ? "Re-optimize" : "Optimize route"}
            </Button>
            {routeRow && (
              <>
                <p className="analysis-tool__note">
                  {routeRow.stops.toLocaleString()} stops{routeRow.totalsLabel ? ` · ${routeRow.totalsLabel}` : ""}
                </p>
                <div className="analysis-tool__row">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeading={<Icon icon={Download} size="sm" />}
                    onClick={exportRoute}
                  >
                    Export ordered list
                  </Button>
                  <Button variant="ghost" size="sm" onClick={removeRoutes}>
                    Clear route
                  </Button>
                </div>
              </>
            )}
            {routeNote && (
              <p className="analysis-tool__note" role="status">
                {routeNote}
              </p>
            )}
          </div>
        ) : (
          lockedCard("routes")
        ))}

      {/* ---------------------------------------------------- Upgrade (once) */}
      {anyLocked && (
        <div className="analysis-upgrade">
          <a className="analysis-upgrade__link" href={upgradeHref}>
            Upgrade your plan
          </a>
          <p className="analysis-tool__hint">Cancel anytime — your maps stay exactly as you left them.</p>
        </div>
      )}

      {/* ------------------------------------------------ Summary / legend */}
      {(territoryRows.length > 0 || radiusRows.length > 0 || driveRows.length > 0) && (
        <div className="analysis-summary">
          <span className="analysis-summary__head">
            <span className="analysis-summary__title">Summary</span>
            {territoryRows.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearTerritories}>
                Clear territories
              </Button>
            )}
          </span>
          {territoryRows.map((t) => (
            <span key={t.id} className={cx("analysis-summary__row")}>
              <Swatch color={t.color} />
              <span className="analysis-summary__label">{t.name}</span>
              <span className="analysis-summary__value">
                {t.count.toLocaleString()}
                {t.sum !== undefined ? ` · ${t.balanceBy ?? "sum"} ${Math.round(t.sum).toLocaleString()}` : ""}
              </span>
              {analysis?.territories?.mode !== "boundary" && (
                <IconButton label={`Remove ${t.name}`} variant="ghost" onClick={() => removeTerritory(t.id)}>
                  <Icon icon={Trash2} size="sm" />
                </IconButton>
              )}
            </span>
          ))}
          {driveRows.map((b) => (
            <span key={b.id} className="analysis-summary__row">
              {b.color ? <Swatch color={b.color} /> : <span />}
              <span className="analysis-summary__label">Drive time</span>
              <span className="analysis-summary__value">{b.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
